<script setup>
import { computed, h } from 'vue';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import HelpFileChip from './HelpFileChip.vue';
import HelpPill from './HelpPill.vue';
import { markdownWithPills, messagePieces, renderMarkdown } from './helpMessage.mjs';
import { isValidInternalRoute } from '../../../help/widgets/types.js';

const props = defineProps({
  message: { type: Object, required: true },
  files: { type: Object, default: () => ({}) },
});

const rendered = computed(() => {
  const { source, pills, files } = markdownWithPills(messagePieces(props.message));
  const html = renderMarkdown(source, { marked, DOMPurify });
  return { body: new DOMParser().parseFromString(html, 'text/html').body, pills, files: files || [] };
});

function attributes(element) {
  const attrs = {};
  for (const attr of element.attributes) attrs[attr.name] = attr.value;
  return attrs;
}

function toVNode(node, pills, files) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const tag = node.tagName.toLowerCase();
  if (tag === 'span' && node.dataset.helpPill !== undefined) {
    const pill = pills[Number(node.dataset.helpPill)];
    return pill ? h(HelpPill, pill) : null;
  }
  if (tag === 'span' && node.dataset.helpFile !== undefined) {
    const file = files[Number(node.dataset.helpFile)];
    if (!file) return null;
    const stored = props.files?.[file.fileId];
    return h(HelpFileChip, {
      label: stored?.Name || file.label,
      path: stored?.Path || '',
      status: stored?.Status || 'ready',
    });
  }
  const href = node.getAttribute('href');
  if (tag === 'a' && href && isValidInternalRoute(href)) {
    return h(HelpPill, { label: node.textContent || href, route: href });
  }
  const attrs = attributes(node);
  if (tag === 'a') {
    attrs.target = '_blank';
    attrs.rel = 'noopener noreferrer';
  }
  return h(tag, attrs, children(node, pills, files));
}

function children(node, pills, files) {
  return [...node.childNodes].map((child) => toVNode(child, pills, files)).filter((child) => child !== null);
}

const Body = () => h('div', { class: 'help-markdown' }, children(rendered.value.body, rendered.value.pills, rendered.value.files));
</script>

<template>
  <Body />
</template>

<style scoped>
.help-markdown {
  overflow-wrap: anywhere;
  line-height: 1.55;
}

.help-markdown > :deep(:first-child) {
  margin-top: 0;
}

.help-markdown > :deep(:last-child) {
  margin-bottom: 0;
}

.help-markdown :deep(p),
.help-markdown :deep(ul),
.help-markdown :deep(ol),
.help-markdown :deep(pre),
.help-markdown :deep(blockquote),
.help-markdown :deep(table) {
  margin: 0.5rem 0;
}

.help-markdown :deep(h1),
.help-markdown :deep(h2),
.help-markdown :deep(h3),
.help-markdown :deep(h4) {
  margin: 0.75rem 0 0.35rem;
  font-weight: 600;
  line-height: 1.3;
}

.help-markdown :deep(h1) { font-size: 1.05rem; }
.help-markdown :deep(h2) { font-size: 1rem; }
.help-markdown :deep(h3),
.help-markdown :deep(h4) { font-size: 0.9rem; }

.help-markdown :deep(ul) {
  list-style: disc;
  padding-left: 1.25rem;
}

.help-markdown :deep(ol) {
  list-style: decimal;
  padding-left: 1.25rem;
}

.help-markdown :deep(li + li) {
  margin-top: 0.15rem;
}

.help-markdown :deep(strong) {
  font-weight: 600;
}

.help-markdown :deep(a) {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.help-markdown :deep(code) {
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
  background: rgb(0 0 0 / 6%);
  font-size: 0.8em;
}

.help-markdown :deep(pre) {
  padding: 0.6rem 0.75rem;
  overflow-x: auto;
  border-radius: 0.375rem;
  background: #111827;
  color: #f9fafb;
}

.help-markdown :deep(pre code) {
  padding: 0;
  background: transparent;
}

.help-markdown :deep(blockquote) {
  padding-left: 0.75rem;
  border-left: 3px solid #d1d5db;
  color: #4b5563;
}

.help-markdown :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
}

.help-markdown :deep(th),
.help-markdown :deep(td) {
  border: 1px solid #d1d5db;
  padding: 0.3rem 0.5rem;
  text-align: left;
  vertical-align: top;
}

.help-markdown :deep(th) {
  background: #f9fafb;
  font-weight: 600;
}

.help-markdown :deep(hr) {
  margin: 0.75rem 0;
  border-color: #e5e7eb;
}
</style>
